/**
 * 포트폴리오 타마고치 펫 "폴리" — 모든 화면 하단 우측에 떠 있는 플로팅 위젯 (Default 레이아웃에 장착)
 * - 데이터: 브리핑 summary + 자산요약 쿼리를 대시보드와 동일 queryKey로 공유 (이미 방문한 페이지면 추가 API 호출 0)
 * - mood 5단계: ecstatic(+0.5%↑) / happy(+0.1~0.5%) / neutral(±0.1%) / sad(-0.1~-0.5%) / crying(-0.5%↓)
 * - 스프라이트: 외곽선(#3f3d56) + 몸통 + 하이라이트 3색 픽셀아트 (박스섀도, 외부 이미지 0개)
 * - 모션: 숨쉬기 / 기분별 애니메이션 / 눈 깜빡임·좌우 훑어보기 / 레벨업 콘페티 / 장 마감 수면(Z)
 * - 물리 반응: 클릭(터치) 시 rAF 댐핑 스프링 — 누르면 아래로 눌리고(중력) 놓으면 반발로 튕겨 오버슈트 후 감쇠
 *   ※ PET_CSS는 JSX 안 <style> 주입 필수 — 누락 시 모든 애니메이션이 무효가 됨 (2026-09-06 실측 버그)
 * - 접기 토글(26-10-22): 카드 좌상단 − 버튼 → 우하단에 🥚 알 탭만 남김(클릭=펼침). localStorage(finhub_pet_collapsed) 유지.
 *   콘텐츠 가림 해제용 — 접어도 쿼리는 유지(대시보드 캐시 신선도 담당). 레거시 동일 컴포넌트와 파일 통째로 동기화
 * - 투자손익률 +5%p마다 레벨업, 세션 내 새 레벨 도달 시 1회 콘페티 (sessionStorage 기준)
 * - 브리핑 순환(26-09-11): 말풍선이 기분 한마디 / 오늘의 주인공(top_gainers) / 예상 배당 / 목표수익률 갭을 15초 간격 순환
 * - 건강 기분(26-09-11): 기분 = 포트폴리오 "전체" 당일 등락 기준 + 30일 daily 시계열로 고점대비 낙폭(drawdown) 계산,
 *   -10% 이상이면 기분 1단계 다운(걱정 모드) — 당일 +인데 고점 대비 깊은 날 폴리가 웃는 오타 방지
 *   (데이터는 전부 대시보드와 동일 queryKey 캐시 공유 — 추가 API 호출 0)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { briefApi } from "src/api/brief";
import { useDefaultUser } from "src/lib/users";
import { useThemeStore } from "src/stores/ThemeStore";
import { useScreenStore } from "src/stores/screenStore";

type Mood = "ecstatic" | "happy" | "neutral" | "sad" | "crying";

/** 픽셀 스프라이트 (14x13) — #=외곽선, b=몸통, w=하이라이트(배+귀안) */
const PX = 4;
const SPRITE_ROWS = [
  "..###....###..",
  "..#b#....#b#..",
  "..#b#....#b#..",
  "..#bb####bb#..",
  ".#bbbbbbbbbb#.",
  "#bbbbbbbbbbbb#",
  "#bbbbbbbbbbbb#",
  "#bbbbwwwwbbbb#",
  "#bbbbwwwwbbbb#",
  ".#bbbwwwwbbb#.",
  ".#bbbbbbbbbb#.",
  "..#bbbbbbbb#..",
  "...##....##...",
];
const SPRITE_W = 14 * PX;
const SPRITE_H = SPRITE_ROWS.length * PX;

const LINE_COLOR = "#3f3d56";
const BELLY_COLOR = "#ffffff";

/** 스프라이트 boxShadow 문자열 생성 (문자별 색상 직접 지정) */
const buildShadows = (body: string): string =>
  SPRITE_ROWS.flatMap((row, y) =>
    [...row].flatMap((ch, x) => {
      if (ch === "#") return [`${x * PX}px ${y * PX}px 0 0 ${LINE_COLOR}`];
      if (ch === "b") return [`${x * PX}px ${y * PX}px 0 0 ${body}`];
      if (ch === "w") return [`${x * PX}px ${y * PX}px 0 0 ${BELLY_COLOR}`];
      return [];
    }),
  ).join(", ");

const MOOD_STYLE: Record<
  Mood,
  { body: string; bg: string; bgDark: string; badge: string; label: string }
> = {
  ecstatic: { body: "#ff8fab", bg: "linear-gradient(135deg,#ffe3ec,#ffc2d1)", bgDark: "linear-gradient(135deg,#5c3444,#7a4258)", badge: "🥳", label: "풀부음" },
  happy: { body: "#ffd166", bg: "linear-gradient(135deg,#fff4d6,#ffe08a)", bgDark: "linear-gradient(135deg,#5c5240,#7a6a4a)", badge: "🙂", label: "상승 중" },
  neutral: { body: "#74c0fc", bg: "linear-gradient(135deg,#e7f3ff,#c2ddff)", bgDark: "linear-gradient(135deg,#2f3f52,#3a4f68)", badge: "😐", label: "지켜보는 중" },
  sad: { body: "#91a7ff", bg: "linear-gradient(135deg,#e9ecff,#ccd4ff)", bgDark: "linear-gradient(135deg,#333a5c,#424d78)", badge: "🥺", label: "소폭 하락" },
  crying: { body: "#748ffc", bg: "linear-gradient(135deg,#e4e9ff,#b9c4ff)", bgDark: "linear-gradient(135deg,#2c3352,#384266)", badge: "😢", label: "급락주의" },
};

/** 걱정 모드 임계값: 30일 고점 대비 이 이상 낙폭이면 기분 1단계 다운 */
const WORRY_DD = -10;

/** 30일 daily 시계열 → 고점 대비 낙폭(%) — 일별 자산가치(순흐름 반영 전 단순 합) 기준 근사 */
const drawdownOf = (daily: { stock: { current_value: number }; fund: { current_value: number }; fixed_income: { current_value: number } }[]): number => {
  if (!daily || daily.length === 0) return 0;
  const values = daily.map((d) => d.stock.current_value + d.fund.current_value + d.fixed_income.current_value);
  const peak = Math.max(...values);
  const last = values[values.length - 1];
  if (peak <= 0) return 0;
  return ((last - peak) / peak) * 100;
};

/** 걱정 모드: 기분을 1단계 낮춤 (ecstatic→happy→neutral→sad→crying) */
const MOOD_LADDER: Mood[] = ["crying", "sad", "neutral", "happy", "ecstatic"];
const worryAdjust = (mood: Mood, worry: boolean): Mood => {
  if (!worry) return mood;
  const idx = MOOD_LADDER.indexOf(mood);
  return MOOD_LADDER[Math.max(0, idx - 1)];
};

const moodOf = (pct: number | null): Mood => {
  if (pct === null) return "neutral";
  if (pct >= 0.5) return "ecstatic";
  if (pct >= 0.1) return "happy";
  if (pct > -0.1) return "neutral";
  if (pct > -0.5) return "sad";
  return "crying";
};

const animOf = (mood: Mood): string =>
  mood === "ecstatic" ? "pet-jump" : mood === "happy" ? "pet-float" : mood === "sad" ? "pet-sink" : mood === "crying" ? "pet-shiver" : "";

/** 걱정 모드 전용 한마디 (drawdown 임계값 도달 시 기분 문구 대신 표시) */
const pickWorryMessage = (dd: number): string =>
  `고점 대비 ${dd.toFixed(1)}%... 괜찮아, 폴리가 같이 버틸게요. 분할 매수도 괜찮아요`;

const pickMessage = (mood: Mood, pct: number | null): string => {
  if (pct === null) return "시세 오는 중... 조금만 기다려주세요!";
  const s = pct >= 0 ? "+" : "";
  switch (mood) {
    case "ecstatic":
      return `오늘 ${s}${pct.toFixed(2)}%! 폴리 신나서 펄쩍펄쩍 (≧▽≦)`;
    case "happy":
      return `${s}${pct.toFixed(2)}% 순항 중! 이대로만 가요~`;
    case "neutral":
      return `오늘은 평온해요 (${s}${pct.toFixed(2)}%). 심심하니 같이 있어요`;
    case "sad":
      return `${pct.toFixed(2)}%... 살짝 아파요. 저 떠밀지 마세요`;
    case "crying":
      return `${pct.toFixed(2)}%?! 떨어지는 제 마음을 달래주세요...`;
  }
};

/** 국내 장마감 여부 (KST 기준, 주말 or 09:00~15:30 외) — 폴리 수면모드 */
const isMarketClosed = (d: Date): boolean => {
  const day = d.getDay();
  if (day === 0 || day === 6) return true;
  const m = d.getHours() * 60 + d.getMinutes();
  return m < 9 * 60 || m > 15 * 60 + 30;
};

/** 콘페티 조각 (결정적 값 — 렌더마다 랜덤 방지) */
const CONFETTI = [
  { color: "#f5222d", tx: "-34px", ty: "-42px", delay: 0 },
  { color: "#fa8c16", tx: "-16px", ty: "-54px", delay: 0.05 },
  { color: "#52c41a", tx: "6px", ty: "-58px", delay: 0.1 },
  { color: "#1677ff", tx: "24px", ty: "-46px", delay: 0.03 },
  { color: "#eb2f96", tx: "38px", ty: "-30px", delay: 0.12 },
  { color: "#faad14", tx: "-44px", ty: "-20px", delay: 0.08 },
  { color: "#722ed1", tx: "48px", ty: "-12px", delay: 0.06 },
  { color: "#13c2c2", tx: "-8px", ty: "-38px", delay: 0.15 },
  { color: "#f5222d", tx: "14px", ty: "-30px", delay: 0.02 },
  { color: "#fa8c16", tx: "-26px", ty: "-50px", delay: 0.11 },
];

/** 쓰다듬기 하트 (결정적 값) */
const HEARTS = [
  { x: -4, y: -42, d: 0 },
  { x: 14, y: -32, d: 0.08 },
  { x: -18, y: -26, d: 0.16 },
  { x: 4, y: -50, d: 0.05 },
];

const PET_CSS = `
.pet-breathe { animation: petBreathe 2.8s ease-in-out infinite; transform-origin: 50% 100%; }
.pet-breathe-slow { animation-duration: 4.5s; }
@keyframes petBreathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.035); } }
.pet-jump { animation: petJump 0.8s ease-in-out infinite; }
@keyframes petJump { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
.pet-float { animation: petFloat 3s ease-in-out infinite; }
@keyframes petFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
.pet-sink { animation: petSink 3s ease-in-out infinite; }
@keyframes petSink { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(2px) rotate(-2deg); } }
.pet-shiver { animation: petShiver 0.35s linear infinite; }
@keyframes petShiver { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-2px); } 75% { transform: translateX(2px); } }
.pet-look { animation: petLook 7s ease-in-out infinite; }
@keyframes petLook { 0%,68%,100% { transform: translateX(0); } 74%,82% { transform: translateX(2px); } 88%,94% { transform: translateX(-2px); } }
.pet-blink { animation: petBlink 4s infinite; }
@keyframes petBlink { 0%,92%,100% { opacity: 1; } 95%,97% { opacity: 0; } }
.pet-tear { animation: petTear 1.6s ease-in infinite; }
@keyframes petTear { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
.pet-heart { position: absolute; font-size: 13px; line-height: 1; animation: petHeart 1.3s ease-out forwards; text-shadow: 0 1px 2px rgba(0,0,0,0.15); }
@keyframes petHeart { 0% { transform: translate(0,0) scale(0.5); opacity: 0; } 25% { opacity: 1; } 100% { transform: translate(var(--hx), var(--hy)) scale(1.15); opacity: 0; } }
.pet-zzz { position: absolute; font-size: 11px; font-weight: 800; color: #7b8cff; animation: petZzz 2.6s ease-out infinite; }
@keyframes petZzz { 0% { transform: translate(0,0); opacity: 0; } 30% { opacity: 1; } 100% { transform: translate(12px,-18px); opacity: 0; } }
.pet-sparkle { position: absolute; color: #ffd43b; font-size: 11px; animation: petSparkle 1.5s ease-in-out infinite; }
@keyframes petSparkle { 0%,100% { opacity: 0.15; transform: scale(0.7) rotate(0deg); } 50% { opacity: 1; transform: scale(1.2) rotate(20deg); } }
.pet-confetti { position: absolute; width: 6px; height: 6px; border-radius: 2px; animation: petConfetti 1.4s ease-out forwards; }
@keyframes petConfetti { 0% { transform: translate(0,0); opacity: 1; } 100% { transform: translate(var(--tx), var(--ty)); opacity: 0; } }
`;

const PortfolioPet = () => {
  const isDark = useThemeStore((s) => s.isDarkMode);
  const screens = useScreenStore((s) => s.screens);
  const defaultUser = useDefaultUser();
  const [userIds] = useState<string[]>([defaultUser.id]);

  // 장마감 체크용 시계 (1분 주기)
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const sleeping = useMemo(() => isMarketClosed(new Date(nowTs)), [nowTs]);

  // 대시보드와 동일 queryKey — 캐시 공유, 추가 대시보드 방문 시 API 0회
  // refetchInterval: 폴리가 상시 떠 있으므로 여기서 주기 갱신 담당 (5분 — 당일 등락이 세션 내에도 따라잡힘)
  const briefQ = useQuery({
    queryKey: ["brief", "summary", userIds],
    queryFn: () => briefApi.getDailyBriefing(userIds, true),
    staleTime: 10 * 60_000,
    refetchInterval: 5 * 60_000,
  });
  const statusQ = useQuery({
    queryKey: ["brief", "status", userIds],
    queryFn: () => briefApi.getDailyAssetStatus(userIds, 30),
    staleTime: 10 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  const loading = briefQ.isLoading || statusQ.isLoading;

  // 당일 등락: 전체 자산 기준 (주식+펀드+머니마켓, 순유입 보정) — 사용자 관점 "오늘 내 자산이 얼마나 늘었나"
  // ※ 주식 페이지 숫자와는 다름: 그쪽은 주식만 + 환율 기준일 분리. 요약 지표는 전체자산이 우선 (26-09-11 캡틴 확정)
  const pct: number | null = briefQ.data ? (briefQ.data.summary?.total?.day_change_percent ?? 0) : null;
  const dayChangeAmt: number | null = briefQ.data ? (briefQ.data.summary?.total?.day_change ?? null) : null;
  const totalInvest = statusQ.data?.summary?.total?.total_investment ?? 0;
  const totalValue = statusQ.data?.summary?.total?.total_current_value ?? 0;
  const totalProfit = totalValue - totalInvest;
  const totalRate = totalInvest > 0 ? (totalProfit / totalInvest) * 100 : 0;

  // 건강 기분: 30일 고점 대비 낙폭으로 걱정 모드 (당일 +여도 깊은 조정 중이면 기분 다운)
  const drawdown = useMemo(() => drawdownOf(statusQ.data?.daily ?? []), [statusQ.data?.daily]);
  const worry = drawdown <= WORRY_DD;

  const mood = worryAdjust(moodOf(pct), worry && pct !== null);
  const style = MOOD_STYLE[mood];
  const worryLabel = worry ? ` · 고점대비 ${drawdown.toFixed(1)}%` : "";
  const moodMessage = worry && pct !== null ? pickWorryMessage(drawdown) : pickMessage(mood, pct);
  const message = sleeping ? "장이 닫혀 있어요 — 폴리 자는 중... 내일도 잘 부탁해요!" : moodMessage;
  const shadows = useMemo(() => buildShadows(style.body), [style.body]);

  // ── 브리핑 순환: 기분 한마디 / 오늘의 주인공 / 예상 배당 / 목표수익률 갭 (15초 간격) ──
  const briefSummary = briefQ.data?.summary;
  type Line = { icon: string; text: string } | null;
  const gain = briefSummary?.top_gainers?.[0];
  const dividend = briefSummary?.dividend;
  const target = briefSummary?.target_return;
  const fmtKrw = (n: number) => n.toLocaleString("ko-KR", { maximumFractionDigits: 0 });

  const briefingLines = useMemo<(Line)[]>(() => {
    if (loading) return [];
    const lines: (Line)[] = [{ icon: "", text: "" }]; // 자리 확보 — 아래에서 moodMessage로 채움
    if (gain) {
      const s = gain.change_percent_krw >= 0 ? "+" : "";
      lines.push({ icon: "🏆", text: `오늘의 주인공: ${gain.stock_name} (${s}${gain.change_percent_krw.toFixed(2)}%)` });
    }
    if (dividend && dividend.month_total_krw > 0) {
      const actual = dividend.month_actual_krw > 0 ? ` · 지급 ${fmtKrw(dividend.month_actual_krw)}원` : "";
      lines.push({ icon: "💰", text: `이번 달 예상 배당 ${fmtKrw(dividend.month_total_krw)}원${actual} (연간 ${fmtKrw(dividend.annual_total_krw)}원)` });
    }
    if (target && target.target_rate > 0) {
      const s = target.gap_rate >= 0 ? "" : "+";
      lines.push({ icon: "🎯", text: `목표수익률 ${target.target_rate}%까지 ${s}${target.gap_rate.toFixed(1)}%p — 폴리 응원해요!` });
    }
    return lines;
  }, [loading, gain?.stock_id, dividend?.month, target?.target_rate, briefQ.data]);

  // 더블클릭: 당일 등락 금액 원화 말풍선 (일반 클릭=쓰다듬기와 공존)
  const [showDayChange, setShowDayChange] = useState(0);
  const onDoubleClick = () => {
    setShowDayChange((c) => c + 1);
    setBubbleOpen(true);
  };
  useEffect(() => {
    if (showDayChange === 0) return;
    const t = setTimeout(() => setShowDayChange(0), 15_000);
    return () => clearTimeout(t);
  }, [showDayChange]);
  const dayChangeText =
    loading || dayChangeAmt === null
      ? "등락 금액 오는 중..."
      : `오늘 폴리 지갑이 ${dayChangeAmt >= 0 ? "+" : ""}${Math.round(dayChangeAmt).toLocaleString("ko-KR")}원 ${dayChangeAmt >= 0 ? "불었어요! (≧▽≦)" : "줄었어요... (ㅠㅠ)"}`;

  const [lineIdx, setLineIdx] = useState(0);
  useEffect(() => {
    if (briefingLines.length <= 1) return;
    const t = setInterval(() => setLineIdx((i) => (i + 1) % briefingLines.length), 15_000);
    return () => clearInterval(t);
  }, [briefingLines.length]);

  // 표시할 말풍선 본문: 더블클릭 시 등락 금액 우선, 아니면 첫 슬롯=기분/걱정 메시지, 이후 브리핑 순환
  const bubbleBody = useMemo(() => {
    if (showDayChange > 0) return dayChangeText;
    if (lineIdx === 0 || briefingLines.length <= 1) return message;
    return briefingLines[lineIdx]?.text ?? message;
  }, [showDayChange, dayChangeText, lineIdx, briefingLines, message]);
  const bubbleIcon = showDayChange > 0 ? (dayChangeAmt !== null && dayChangeAmt >= 0 ? "📈" : "📉") : lineIdx === 0 || briefingLines.length <= 1 ? "" : (briefingLines[lineIdx]?.icon ?? "");

  // 레벨: 투자손익률 +5%p마다 1업 (최소 1)
  const level = Math.max(1, Math.floor(totalRate / 5) + 1);

  // 말풍선: 메시지/쓰다듬기 변화 시 재표시, 15초 후 자동 숨김
  const [bubbleOpen, setBubbleOpen] = useState(false);
  const [petCount, setPetCount] = useState(0);
  useEffect(() => {
    setBubbleOpen(true);
    const t = setTimeout(() => setBubbleOpen(false), 15_000);
    return () => clearTimeout(t);
  }, [message, petCount]);

  // ── 물리 스프링 (rAF 댐핑 스프링: 누르면 아래로, 놓으면 반발 튕김) ──
  const physRef = useRef<HTMLDivElement | null>(null);
  const spring = useRef({ y: 0, v: 0, running: false, raf: 0, last: 0 });
  const K = 260; // 스프링 상수 (경도)
  const C = 11; // 감쇠 계수 — 낮아야 반발(오버슈트)이 생김

  const applyTransform = () => {
    const el = physRef.current;
    if (!el) return;
    const { y } = spring.current;
    const scaleY = Math.max(0.86, Math.min(1.08, 1 - y / 45)); // 눌리면 납작, 늘어나면 홀쭉
    const scaleX = Math.max(0.94, Math.min(1.06, 1 + y / 70)); // 부피 보존 느낌
    el.style.transform = `translateY(${y.toFixed(2)}px) scale(${scaleX.toFixed(3)}, ${scaleY.toFixed(3)})`;
  };

  const springTick = (t: number) => {
    const s = spring.current;
    const dt = Math.min((t - s.last) / 1000, 0.033) || 0.016;
    s.last = t;
    const a = -K * s.y - C * s.v;
    s.v += a * dt;
    s.y += s.v * dt;
    if (Math.abs(s.y) < 0.05 && Math.abs(s.v) < 0.5) {
      s.y = 0;
      s.v = 0;
      s.running = false;
      applyTransform();
      return;
    }
    applyTransform();
    s.raf = requestAnimationFrame(springTick);
  };

  /** 힘 가하기: v=속도(px/s, +아래/−위), y=즉시 변위 */
  const kick = (v: number, y?: number) => {
    const s = spring.current;
    s.v = v;
    if (y !== undefined) s.y = y;
    if (!s.running) {
      s.running = true;
      s.last = performance.now();
      s.raf = requestAnimationFrame(springTick);
    }
  };

  useEffect(() => () => cancelAnimationFrame(spring.current.raf), []);

  // 포인터 이벤트: 누르는 동안 중력으로 눌림 → 놓으면 반발로 튕겨 오름
  const [pressed, setPressed] = useState(false);
  const onPointerDown = () => {
    setPressed(true);
    kick(320, 4); // 아래로 찹 눌림 (중력)
  };
  const onPointerUp = () => {
    if (!pressed) return;
    setPressed(false);
    kick(-420); // 반발 — 위로 힘껏 튕겨 오버슈트 후 감쇠
    setPetCount((c) => c + 1); // 쓰다듬기 하트
    setBubbleOpen(true);
  };
  const onPointerLeave = () => {
    if (pressed) {
      setPressed(false);
      kick(-260); // 드래그로 이탈 시 약한 반발
    }
  };
  useEffect(() => {
    if (petCount === 0) return;
    const t = setTimeout(() => setPetCount(0), 1_600);
    return () => clearTimeout(t);
  }, [petCount]);

  // 레벨업 콘페티: sessionStorage의 마지막 레벨보다 커질 때 세션 내 1회
  const [confetti, setConfetti] = useState(0);
  useEffect(() => {
    if (loading) return;
    const stored = Number(sessionStorage.getItem("finhub_pet_level") || "0");
    if (stored > 0 && level > stored) setConfetti((c) => c + 1);
    if (level !== stored) sessionStorage.setItem("finhub_pet_level", String(level));
  }, [level, loading]);
  useEffect(() => {
    if (confetti === 0) return;
    const t = setTimeout(() => setConfetti(0), 1_700);
    return () => clearTimeout(t);
  }, [confetti]);

  const pctColor = pct !== null && pct >= 0 ? "var(--fred)" : "var(--fblue)";
  const bubbleBg = isDark ? "#2a2a2a" : "#fff";
  const bubbleColor = isDark ? "#ddd" : "#444";
  const bubbleBorder = isDark ? "#444" : "#e8e8e8";

  // 접기 토글: 우하단 고정 위젯이 콘텐츠를 가릴 때 알로 접기 — localStorage 유지 (새로고침해도 접힘)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("finhub_pet_collapsed") === "1");
  useEffect(() => {
    localStorage.setItem("finhub_pet_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  // 접힘: 작은 알 탭만 표시 — 클릭 시 펼침 (쿼리는 유지: 대시보드 캐시 신선도 담당)
  if (collapsed) {
    return (
      <div style={{ position: "fixed", right: screens.md ? 20 : 12, bottom: screens.md ? 18 : 14, zIndex: 900 }}>
        <style>{PET_CSS}</style>
        <div
          title="폴리 펼치기"
          onClick={() => setCollapsed(false)}
          className="pet-breathe pet-breathe-slow"
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: isDark ? style.bgDark : style.bg,
            border: `2px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.06)"}`,
            boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            cursor: "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          🥚
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        right: screens.md ? 20 : 12,
        bottom: screens.md ? 18 : 14,
        zIndex: 900, // AntD Modal/Drawer(1000) 아래 — 오버레이는 폴리를 덮음
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 8,
      }}
    >
      {/* 키프레임 CSS 주입 — 이 태그가 빠지면 모든 애니메이션 무효 (2026-09-06 실측 버그) */}
      <style>{PET_CSS}</style>

      {/* 말풍선 */}
      {bubbleOpen && (
        <div
          onClick={() => setBubbleOpen(false)}
          style={{
            maxWidth: screens.md ? 240 : 200,
            fontSize: 12,
            lineHeight: 1.5,
            color: bubbleColor,
            background: bubbleBg,
            border: `1px solid ${bubbleBorder}`,
            borderRadius: 10,
            padding: "7px 11px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.14)",
            cursor: "pointer",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            폴리 <span style={{ color: "#999", fontWeight: 500 }}>Lv.{level}</span>{" "}
            {sleeping ? "💤 취침" : `${style.badge} ${style.label}${worryLabel}`}
          </div>
          {bubbleIcon && <span style={{ marginRight: 4 }}>{bubbleIcon}</span>}
          {bubbleBody}
        </div>
      )}

      {/* 펫 컨테이너 (누르면 중력으로 눌림, 놓으면 반발 튕김, 호버 = 말풍선) */}
      <div
        title="폴리 쓰다듬기 (더블클릭: 오늘 등락 금액)"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onDoubleClick={onDoubleClick}
        onMouseEnter={() => setBubbleOpen(true)}
        style={{
          position: "relative",
          width: 72,
          height: 68,
          borderRadius: 18,
          background: isDark ? style.bgDark : style.bg,
          border: `2px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.06)"}`,
          boxShadow: pressed ? "0 2px 8px rgba(0,0,0,0.28)" : "0 4px 16px rgba(0,0,0,0.22)",
          cursor: "pointer",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          userSelect: "none",
          WebkitUserSelect: "none",
          touchAction: "manipulation",
          transition: "box-shadow 0.15s",
        }}
      >
        {/* 접기 버튼 — 콘텐츠 가림 해제용. 펫 물리(눌림/튕김)·더블클릭 이벤트 전파 차단 */}
        <div
          title="폴리 접기"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed(true);
          }}
          style={{
            position: "absolute",
            top: -8,
            left: -10,
            width: 18,
            height: 18,
            borderRadius: "50%",
            fontSize: 11,
            fontWeight: 700,
            color: bubbleColor,
            background: isDark ? "#2f2f2f" : "#fff",
            border: `1px solid ${bubbleBorder}`,
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            cursor: "pointer",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          −
        </div>

        {/* 당일 등락 배지 */}
        <div
          style={{
            position: "absolute",
            top: -8,
            right: -10,
            fontSize: 10,
            fontWeight: 700,
            color: pctColor,
            background: isDark ? "#2f2f2f" : "#fff",
            border: `1px solid ${bubbleBorder}`,
            borderRadius: 10,
            padding: "1px 6px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            whiteSpace: "nowrap",
          }}
        >
          {loading || pct === null ? "-" : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
        </div>

        {/* 물리 레이어 — 스프링 변위/스퀴시 적용 (발밑 기준) */}
        <div
          ref={physRef}
          style={{
            position: "relative",
            width: SPRITE_W,
            height: SPRITE_H,
            transformOrigin: "50% 100%",
            marginBottom: 4,
            willChange: "transform",
          }}
        >
          {/* 숨쉬기 (수면 시 느리게) */}
          <div className={`pet-breathe${sleeping ? " pet-breathe-slow" : ""}`} style={{ position: "absolute", inset: 0 }}>
            {/* 기분별 애니메이션 (수면 시 정지) */}
            <div className={sleeping ? "" : animOf(mood)} style={{ position: "absolute", inset: 0 }}>
              {/* 바디+외곽선 (박스섀도 픽셀아트) */}
              <div
                style={
                  {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: PX,
                    height: PX,
                    boxShadow: shadows,
                  } as React.CSSProperties
                }
              />

              {/* 귀 안쪽 */}
              <span style={{ position: "absolute", left: 13, top: 5, width: 2, height: 7, borderRadius: 2, background: "#ff7aa2", opacity: 0.9 }} />
              <span style={{ position: "absolute", left: 41, top: 5, width: 2, height: 7, borderRadius: 2, background: "#ff7aa2", opacity: 0.9 }} />

              {/* 눈 — 수면 시 감은 눈, 아니면 좌우 훑어보기+깜빡임 */}
              {sleeping ? (
                <>
                  <span style={{ position: "absolute", left: 10, top: 26, width: 7, height: 3, borderRadius: 2, background: "#2b2b3d" }} />
                  <span style={{ position: "absolute", left: 39, top: 26, width: 7, height: 3, borderRadius: 2, background: "#2b2b3d" }} />
                </>
              ) : (
                <div className="pet-look" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                  <span className="pet-blink" style={{ position: "absolute", left: 11, top: 23, width: 5, height: 7, borderRadius: 2, background: "#2b2b3d" }} />
                  <span style={{ position: "absolute", left: 12, top: 24, width: 2, height: 2, borderRadius: 1, background: "#fff", zIndex: 1 }} />
                  <span className="pet-blink" style={{ position: "absolute", left: 40, top: 23, width: 5, height: 7, borderRadius: 2, background: "#2b2b3d" }} />
                  <span style={{ position: "absolute", left: 41, top: 24, width: 2, height: 2, borderRadius: 1, background: "#fff", zIndex: 1 }} />
                </div>
              )}

              {/* 입 — 수면 시 꿀꺽(o), 기분별 표정 */}
              {sleeping ? (
                <span style={{ position: "absolute", left: 25, top: 31, width: 6, height: 6, borderRadius: "50%", border: "2px solid #2b2b3d" }} />
              ) : mood === "happy" || mood === "ecstatic" ? (
                <span style={{ position: "absolute", left: 22, top: 29, width: 12, height: 6, borderBottom: `3px solid #2b2b3d`, borderRadius: "0 0 12px 12px" }} />
              ) : mood === "sad" || mood === "crying" ? (
                <span style={{ position: "absolute", left: 23, top: 31, width: 10, height: 5, borderTop: `3px solid #2b2b3d`, borderRadius: "12px 12px 0 0" }} />
              ) : (
                <span style={{ position: "absolute", left: 23, top: 32, width: 10, height: 3, borderRadius: 2, background: "#2b2b3d" }} />
              )}

              {/* 볼터치 (최고 기분) */}
              {mood === "ecstatic" && !sleeping && (
                <>
                  <span style={{ position: "absolute", left: 6, top: 29, width: 7, height: 4, borderRadius: 2, background: "#ff5c8a", opacity: 0.55 }} />
                  <span style={{ position: "absolute", left: 43, top: 29, width: 7, height: 4, borderRadius: 2, background: "#ff5c8a", opacity: 0.55 }} />
                </>
              )}

              {/* 눈물 (울음) */}
              {mood === "crying" && !sleeping && (
                <span className="pet-tear" style={{ position: "absolute", left: 42, top: 30, width: 5, height: 7, borderRadius: 3, background: "#4dabf7" }} />
              )}

              {/* 수면 Zzz */}
              {sleeping && (
                <>
                  <span className="pet-zzz" style={{ left: 46, top: -2 }}>Z</span>
                  <span className="pet-zzz" style={{ left: 52, top: 4, animationDelay: "1.3s", fontSize: 9 }}>Z</span>
                </>
              )}

              {/* 신남 반짝임 */}
              {mood === "ecstatic" && !sleeping && (
                <>
                  <span className="pet-sparkle" style={{ left: -9, top: 2 }}>✦</span>
                  <span className="pet-sparkle" style={{ right: -11, top: 16, animationDelay: "0.5s" }}>✦</span>
                </>
              )}

              {/* 쓰다듬기 하트 */}
              {petCount > 0 && (
                <div key={petCount} style={{ position: "absolute", left: 30, top: 14, pointerEvents: "none" }}>
                  {HEARTS.map((h, i) => (
                    <span
                      key={i}
                      className="pet-heart"
                      style={{ color: "#ff6b9d", animationDelay: `${h.d}s`, "--hx": `${h.x}px`, "--hy": `${h.y}px` } as React.CSSProperties}
                    >
                      ♥
                    </span>
                  ))}
                </div>
              )}

              {/* 레벨업 콘페티 */}
              {confetti > 0 && (
                <div key={confetti} style={{ position: "absolute", left: 28, top: 30, pointerEvents: "none" }}>
                  {CONFETTI.map((c, i) => (
                    <span
                      key={i}
                      className="pet-confetti"
                      style={{ background: c.color, animationDelay: `${c.delay}s`, "--tx": c.tx, "--ty": c.ty } as React.CSSProperties}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPet;
